import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { ResponsiveDialog, ResponsiveDialogHeader, ResponsiveDialogBody, ResponsiveDialogFooter } from './ResponsiveDialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LineItemsEditor } from './LineItemsEditor';
import { ClientPicker } from './ClientPicker';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { toInputDate, fromInputDate } from '../lib/format';

const blank = {
  client_id: '',
  quotation_date: '',
  event_date: '',
  valid_until_date: '',
  venue: '',
  event_description: '',
  status: 'Draft',
  notes: '',
  terms_conditions: '',
  items: [],
  final_discount_type: 'nominal',
  final_discount_value: '0',
};

export const QuotationFormDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/clients').then(r => setClients(r.data));
      api.get('/settings').then(r => {
        if (!editing) {
          setForm(f => ({ ...f, terms_conditions: r.data?.quotation_terms || '' }));
        }
      });
    }
    // eslint-disable-next-line
  }, [open]);

  const refreshClients = async () => {
    const { data } = await api.get('/clients');
    setClients(data);
  };

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        client_id: editing.client_id || '',
        quotation_date: toInputDate(editing.quotation_date),
        event_date: toInputDate(editing.event_date),
        valid_until_date: toInputDate(editing.valid_until_date),
        venue: editing.venue || '',
        event_description: editing.event_description || '',
        status: editing.status || 'Draft',
        notes: editing.notes || '',
        terms_conditions: editing.terms_conditions || '',
        items: (editing.items || []).map(it => ({ ...it })),
        final_discount_type: editing.final_discount_type || 'nominal',
        final_discount_value: editing.final_discount_value != null ? String(editing.final_discount_value) : '0',
      });
    } else {
      setForm({ ...blank, quotation_date: toInputDate(new Date()) });
    }
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error('Pilih client'); return; }
    if (form.items.length === 0) { toast.error('Minimal 1 item'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quotation_date: fromInputDate(form.quotation_date),
        event_date: fromInputDate(form.event_date),
        valid_until_date: fromInputDate(form.valid_until_date),
        final_discount_type: (form.final_discount_type || 'nominal').toLowerCase(),
        final_discount_value: Number(form.final_discount_value) || 0,
        items: form.items.map(it => ({
          item_name: it.item_name,
          description: it.description || '',
          quantity: Number(it.quantity) || 0,
          unit: it.unit || '',
          unit_price: Number(it.unit_price) || 0,
          discount: Number(it.discount) || 0,
          product_id: it.product_id || null,
          package_id: it.package_id || null,
          package_group_name: it.package_group_name || null,
          item_type: it.item_type || 'custom',
        })),
      };
      if (editing) {
        await api.put(`/quotations/${editing.id}`, payload);
        toast.success('Penawaran diperbarui');
      } else {
        await api.post('/quotations', payload);
        toast.success('Penawaran dibuat');
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange} testid="quotation-form-dialog" size="xl">
      <form onSubmit={submit} className="flex flex-col min-h-0 flex-1 h-full">
        <ResponsiveDialogHeader
          title={editing ? `Edit Penawaran ${editing.quotation_number}` : 'Penawaran Baru'}
          description="Lengkapi detail penawaran dan item yang dijual."
        />
        <ResponsiveDialogBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Client *</Label>
              <ClientPicker
                value={form.client_id}
                onChange={(v) => setForm({ ...form, client_id: v })}
                clients={clients}
                onClientsRefresh={refreshClients}
                testid="quotation-form-client"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-11 md:h-9 text-base md:text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Sent">Terkirim</SelectItem>
                  <SelectItem value="Approved">Disetujui</SelectItem>
                  <SelectItem value="Rejected">Ditolak</SelectItem>
                  <SelectItem value="Expired">Kadaluarsa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tanggal Penawaran</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm date-input-mobile" type="date" value={form.quotation_date} onChange={(e) => setForm({ ...form, quotation_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Berlaku Hingga</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm date-input-mobile" type="date" value={form.valid_until_date} onChange={(e) => setForm({ ...form, valid_until_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tanggal Event</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm date-input-mobile" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Venue</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Deskripsi Event</Label>
              <Input className="h-11 md:h-9 text-base md:text-sm" value={form.event_description} onChange={(e) => setForm({ ...form, event_description: e.target.value })} />
            </div>
          </div>

          <LineItemsEditor
            items={form.items}
            onChange={(items) => setForm({ ...form, items })}
            testidPrefix="quotation-item"
            finalDiscount={{ type: form.final_discount_type, value: form.final_discount_value }}
            onFinalDiscountChange={(fd) => setForm({ ...form, final_discount_type: fd.type, final_discount_value: fd.value })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Catatan</Label>
              <Textarea rows={3} className="text-base md:text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Syarat & Ketentuan</Label>
              <Textarea rows={3} className="text-base md:text-sm" value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} />
            </div>
          </div>
        </ResponsiveDialogBody>
        <ResponsiveDialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="h-11 md:h-9 w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Batal
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="h-11 md:h-9 w-full sm:w-auto"
            data-testid="quotation-form-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan
          </Button>
        </ResponsiveDialogFooter>
      </form>
    </ResponsiveDialog>
  );
};
